import React, { useEffect, useMemo, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import LayoutWrapper from "@iso/components/utility/layoutWrapper.js";
import Box from "@iso/components/utility/box";
import SprayPage from "./Spray.styles";
import AgronomistAdmin from "./AgronomistAdmin";
import { Row, Col, Table, Button, Spin, Card, Tabs, Tag, Space, Select } from "antd";
import IntlMessages from "@iso/components/utility/intlMessages";
import { useSelector, useDispatch } from "react-redux";
import sprayActions from "../../../redux/spray/actions";
import { JOB_TYPES } from '@iso/constants/jobTypes';
import { formatSprayJobDisplayName } from '@iso/constants/sprayJobHelpers';
import {
  sprayJobStates,
  getSprayJobStatusLabel,
  getSprayJobTagColor,
} from '@iso/constants/sprayJobStates';
import useSprayPermissions from '../../../hooks/useSprayPermissions';
import { sprayService } from '@iso/services';

const { TabPane } = Tabs;

/**
 * Map a job's current status to the route the action button should navigate to.
 * Damien's redesign moves every per-status workflow out of modal-land and into
 * dedicated full-page routes.
 */
// Round-4 unified workflow (Damien): every active-status route now lands
// on the spray-job detail page, which renders the Actions panel that
// houses Prepare / per-zone Start-Pause-Finish / refill / cleanup as a
// single stacked list. The per-status full-page routes (/prepare,
// /spray, /cleanup) still exist as legacy fallbacks reachable by direct
// URL but the dashboard no longer points at them.
const getActionRoute = (job) => `/spray-job/${job.id}`;

const Spray = () => {
  const routerHistory = useHistory();
  const dispatch = useDispatch();
  const { loading, toBeStarted, prepared, spraying, history } = useSelector(
    (state) => state.spray
  );
  const propertyId = useSelector((state) => state.property.activePropertyId);
  const { isPrivileged } = useSprayPermissions();

  // Status filter (Damien feedback: the unified active-jobs table got cluttered
  // when several jobs sit in the same status; this lets the user narrow down).
  // 'all' shows everything; otherwise we filter to that single status.
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    if (propertyId) {
      dispatch(sprayActions.fetchSprayJobs(propertyId));
    }
  }, [propertyId, dispatch]);

  // Active jobs — flattened across the old kanban columns plus any in cleanup
  // or waiting_for_completion that the dashboard endpoint returns.
  // Sorted by most recent activity (updated_at desc, falling back to
  // created_at) so the user sees what's been touched most recently first,
  // rather than the previous implicit ordering by status bucket.
  const allActiveJobs = useMemo(() => {
    const merged = [...(toBeStarted || []), ...(prepared || []), ...(spraying || [])];
    const ts = (j) => {
      const value = j?.updated_at || j?.created_at;
      if (!value) return 0;
      const t = new Date(value).getTime();
      return Number.isFinite(t) ? t : 0;
    };
    return merged.sort((a, b) => ts(b) - ts(a));
  }, [toBeStarted, prepared, spraying]);

  // Apply the status filter on top of the flattened active-jobs list.
  const activeJobs = useMemo(() => {
    if (statusFilter === 'all') return allActiveJobs;
    return allActiveJobs.filter((j) => j.status === statusFilter);
  }, [allActiveJobs, statusFilter]);

  // Counts per status, used in the filter dropdown labels so users can see at
  // a glance how many jobs sit in each bucket without expanding the filter.
  const statusCounts = useMemo(() => {
    return allActiveJobs.reduce((acc, j) => {
      acc[j.status] = (acc[j.status] || 0) + 1;
      return acc;
    }, {});
  }, [allActiveJobs]);

  // Build the dropdown options — restrict to statuses that can appear in the
  // Active Jobs table (i.e. non-terminal states).
  const filterableStatuses = sprayJobStates.filter(
    (s) => s.value !== 'completed' && s.value !== 'cancelled'
  );

  /**
   * Format a job's created_at timestamp for the dashboard. We deliberately
   * read created_at (not start_time) so refilled jobs — which are cloned
   * with a fresh created_at when the operator refills — sort and label by
   * the moment the new job appeared, not the original schedule.
   */
  const formatCreatedAt = (job) => {
    if (!job?.created_at) return '—';
    const d = new Date(job.created_at);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString('en-AU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const renderActionButton = (job, allJobs) => {
    const stateMeta = sprayJobStates.find((s) => s.value === job.status);
    const actionLabel = stateMeta?.action;
    if (!actionLabel) return null;
    return (
      <Button
        type="primary"
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          routerHistory.push(getActionRoute(job));
        }}
      >
        {actionLabel}
      </Button>
    );
  };

  const activeColumns = [
    {
      title: "Job",
      dataIndex: "job_name",
      key: "job_name",
      render: (_, record) => formatSprayJobDisplayName(record, activeJobs),
    },
    {
      title: "Type",
      dataIndex: "job_type",
      key: "job_type",
      render: (text) => JOB_TYPES.find((t) => t.value === text)?.label || text,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (text) => (
        <Tag color={getSprayJobTagColor(text)}>{getSprayJobStatusLabel(text)}</Tag>
      ),
    },
    {
      title: "Date Created",
      key: "created_at",
      render: (_, record) => <span style={{ color: '#666' }}>{formatCreatedAt(record)}</span>,
    },
    {
      title: "Action",
      key: "action",
      align: 'right',
      render: (_, record) => renderActionButton(record, activeJobs),
    },
  ];

  const historyColumns = [
    {
      title: "Date of spraying",
      dataIndex: "start_time",
      key: "start_time",
    },
    {
      title: "End date",
      dataIndex: "end_time",
      key: "end_time",
    },
    {
      title: "Job",
      dataIndex: "job_name",
      key: "job_name",
      render: (text, record) => formatSprayJobDisplayName(record, history),
    },
    {
      title: "Spray type",
      dataIndex: "job_type",
      key: "job_type",
      render: (text) => JOB_TYPES.find((t) => t.value === text)?.label || text,
    },
    {
      title: "Solution Used",
      dataIndex: "solution_used",
      key: "solution_used",
    },
    {
      title: "Responsible",
      dataIndex: "responsible",
      key: "responsible",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (text) => (
        <Tag color={getSprayJobTagColor(text)}>{getSprayJobStatusLabel(text)}</Tag>
      ),
    },
    {
      title: "Report",
      key: "report",
      align: 'right',
      render: (_, record) => {
        if (record.status !== 'completed') return null;
        return (
          <Button
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              sprayService.downloadReportPdf(record.id).catch(() => {
                // surfaced via console; UI doesn't need a banner for a missed PDF
                console.error('Failed to download spray job report');
              });
            }}
          >
            PDF
          </Button>
        );
      },
    },
  ];

  const renderDashboard = () => (
    <>
      {loading ? (
        <div style={{ textAlign: "center", margin: "40px 0" }}>
          <Spin size="large" />
        </div>
      ) : (
        <>
          <Card
            title="Active Spray Jobs"
            variant="borderless"
            style={{ borderRadius: "8px", marginBottom: 16 }}
            extra={
              <Space size="small">
                <span style={{ color: '#666' }}>Filter by status:</span>
                <Select
                  size="small"
                  style={{ minWidth: 200 }}
                  value={statusFilter}
                  onChange={setStatusFilter}
                  options={[
                    {
                      value: 'all',
                      label: `All (${allActiveJobs.length})`,
                    },
                    ...filterableStatuses.map((s) => ({
                      value: s.value,
                      label: `${s.label} (${statusCounts[s.value] || 0})`,
                    })),
                  ]}
                />
              </Space>
            }
          >
            <div className="table-responsive">
              <Table
                columns={activeColumns}
                dataSource={activeJobs}
                rowKey="id"
                scroll={{ x: "max-content" }}
                locale={{ emptyText: 'No active spray jobs' }}
                onRow={(record) => ({
                  onClick: () => routerHistory.push(`/spray-job/${record.id}`),
                  style: { cursor: 'pointer' },
                })}
                pagination={{
                  responsive: true,
                  showSizeChanger: true,
                  pageSizeOptions: ["10", "20", "50"],
                  defaultPageSize: 20,
                }}
              />
            </div>
          </Card>

          <Card
            title="Spray History"
            variant="borderless"
            style={{ borderRadius: "8px" }}
          >
            <div className="table-responsive">
              <Table
                columns={historyColumns}
                dataSource={history}
                rowKey="id"
                scroll={{ x: "max-content" }}
                onRow={(record) => ({
                  onClick: () => routerHistory.push(`/spray-job/${record.id}`),
                  style: { cursor: 'pointer' },
                })}
                pagination={{
                  responsive: true,
                  showSizeChanger: true,
                  pageSizeOptions: ["10", "20", "50"],
                }}
              />
            </div>
          </Card>
        </>
      )}
    </>
  );

  return (
    <LayoutWrapper>
      <SprayPage>
        <Box id="containerBox">
          <div className="manage-property-header">
            <div className="mp-title">
              <h2 className="manage-property-title">Spray Module</h2>
            </div>

            <div className="button-group">
              <Button type="primary" className="btn-success">
                <Link to="/spray-job/create">
                  <IntlMessages id="sprayPage.managePage.addSprayJob" />
                </Link>
              </Button>
            </div>
          </div>
        </Box>

        {isPrivileged ? (
          <Tabs defaultActiveKey="dashboard" size="large">
            <TabPane tab="Spray Dashboard" key="dashboard">
              {renderDashboard()}
            </TabPane>
            <TabPane tab="Agronomist Admin" key="admin">
              <AgronomistAdmin />
            </TabPane>
          </Tabs>
        ) : (
          renderDashboard()
        )}
      </SprayPage>
    </LayoutWrapper>
  );
};

export default Spray;
